import { useApolloClient, useQuery } from '@apollo/client';
import { GridColumnsInitialState, GridEventListener, GridValidRowModel } from '@mui/x-data-grid-pro';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { LOAD_COLUMN_CONFIG_QUERY } from './LoadQuery.gql';
import { SAVE_COLUMN_CONFIG_MUTATION } from './SaveQuery.gql';
import {
  UseDataGridRemoteStateGridProps,
  UseDataGridRemoteStateProps,
  UseDataGridRemoteStateResult,
  UseDataGridRemoteStateUpdateReason,
} from './useDataGridRemoteState.types';
import {
  getDataGridState,
  getInitialState,
  pickPersistableState,
  stateFromConfig,
} from './useDataGridRemoteState.util';

/**
 * safe/load the state from DB
 */
export const useDataGridRemoteState = <R extends GridValidRowModel = any, V = any, F = V>({
  domain,
  apiRef,
  config,
  skip,
}: UseDataGridRemoteStateProps<R, V, F>): UseDataGridRemoteStateResult<R> => {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(getInitialState(config));
  const { onError } = useCustomSnackbar();

  const client = useApolloClient();

  const { data: queryData, loading: queryLoading } = useQuery(LOAD_COLUMN_CONFIG_QUERY, {
    variables: { domain: domain } as any,
    onError,
    skip,
  });

  const { remoteStateDefault, remoteStateUser } = useMemo(() => {
    const remoteStateList = queryData && queryData.baseColumnConfig;
    return {
      remoteStateDefault: remoteStateList?.find((item) => !item.userId)?.config,
      remoteStateUser: remoteStateList?.find((item) => item.userId)?.config,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData]);

  useEffect(() => {
    const { updateReason } = state;
    if (updateReason === UseDataGridRemoteStateUpdateReason.USER) {
      (async () => {
        try {
          await client.mutate({
            mutation: SAVE_COLUMN_CONFIG_MUTATION,
            variables: {
              domain,
              config: pickPersistableState(state),
            },
          });
        } catch (error) {
          console.log('useDataGridRemoteState save error', error);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const onColumnVisibilityModelChange: NonNullable<UseDataGridRemoteStateGridProps['onColumnVisibilityModelChange']> =
    useCallback((columnVisibility) => {
      setState((prev) => ({
        ...prev,
        columnVisibility,
        updateReason: UseDataGridRemoteStateUpdateReason.USER,
      }));
    }, []);

  const onSortModelChange: NonNullable<UseDataGridRemoteStateGridProps['onSortModelChange']> = useCallback((sort) => {
    setState((prev) => ({
      ...prev,
      sort,
      updateReason: UseDataGridRemoteStateUpdateReason.USER,
    }));
  }, []);

  const onColumnOrderChange: NonNullable<UseDataGridRemoteStateGridProps['onColumnOrderChange']> = useCallback(() => {
    const orderedFields = apiRef.current.exportState({ exportOnlyDirtyModels: true })?.columns?.orderedFields;
    if (orderedFields) {
      setState((prev) => ({
        ...prev,
        columnOrder: orderedFields,
        updateReason: UseDataGridRemoteStateUpdateReason.USER,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onColumnWidthChange = useCallback<GridEventListener<'columnWidthChange'>>((params) => {
    setState((prev) => ({
      ...prev,
      updateReason: UseDataGridRemoteStateUpdateReason.USER,
      columnWidth: {
        ...prev.columnWidth,
        [params.colDef.field]: params.width,
      },
    }));
  }, []);

  const onDensityChange: UseDataGridRemoteStateResult['onDensityChange'] = useCallback((density) => {
    setState((prev) => ({
      ...prev,
      density,
      updateReason: UseDataGridRemoteStateUpdateReason.USER,
    }));
  }, []);

  const onRemoteStateReset: UseDataGridRemoteStateResult['onRemoteStateReset'] = useCallback(() => {
    if (remoteStateDefault) {
      const nextState = stateFromConfig(remoteStateDefault, config.columns);
      setState((prev) => ({
        ...prev,
        ...nextState,
        updateReason: UseDataGridRemoteStateUpdateReason.USER,
      }));
      if (apiRef.current) {
        apiRef.current.restoreState(getDataGridState(nextState));
      }
    } else {
      setState(() => ({
        ...getInitialState(config),
        updateReason: UseDataGridRemoteStateUpdateReason.USER,
      }));
      if (apiRef.current) {
        const exportedState = apiRef.current.exportState();

        const resettedState = {
          ...exportedState,
          columns: {
            ...exportedState.columns,
            dimensions: config.columns.reduce<NonNullable<GridColumnsInitialState['dimensions']>>((acc, col) => {
              acc[col.field] = { width: col.width };
              return acc;
            }, {}),
            orderedFields: config.columns.map((colDef) => colDef.field),
          },
        };
        apiRef.current.restoreState(resettedState);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, remoteStateDefault]);

  // Sync state with config defaults if config changes before remote/user state loads
  // This handles cases where config isn't ready on first mount
  // Once remote or user state is set, config changes are ignored (remote/user takes precedence)
  useEffect(() => {
    if (state.updateReason === UseDataGridRemoteStateUpdateReason.INITIAL) {
      setState(getInitialState(config));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // update state if remote config loaded
  useEffect(() => {
    const loadedConfig = remoteStateUser || remoteStateDefault;
    if (loadedConfig) {
      setState((prev) => ({
        ...prev,
        ...stateFromConfig(loadedConfig, config.columns),
        updateReason: UseDataGridRemoteStateUpdateReason.REMOTE,
      }));
    }
    setLoading(() => queryLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteStateDefault, remoteStateUser, queryLoading]);

  // columnWidth/columnOrder only for setting initialState
  // because there's no controlled prop yet and changing these
  // would conflict with data-grid internal state
  const initialState: UseDataGridRemoteStateGridProps<R>['initialState'] = useMemo(
    () =>
      getDataGridState({
        columnOrder: state.columnOrder,
        columnWidth: state.columnWidth,
      }),
    [state.columnOrder, state.columnWidth],
  );

  return {
    loading,
    gridProps: {
      columns: config.columns,
      columnGroupingModel: config.columnGroupingModel,
      density: state.density,
      columnVisibilityModel: state.columnVisibility,
      sortModel: state.sort,
      initialState,
      onColumnVisibilityModelChange,
      onSortModelChange,
      onColumnOrderChange,
      onColumnWidthChange,
    },
    onDensityChange,
    onRemoteStateReset,
  };
};
